import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Edit2, Check, X, ListTodo, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface Task {
  id: string;
  title: string;
  description?: string;
  priority: number;
  completed: boolean;
  completed_date?: string;
  assigned_to?: string;
  assignee_name?: string;
  due_date?: string;
  project?: string;
  tags?: string[];
  created_date: string;
  modified_date: string;
  campaign_id?: string | null;
}

interface CampaignOption {
  id: string;
  name: string;
}

export const TasksCRUD = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [filterCompleted, setFilterCompleted] = useState<'all' | 'active' | 'completed'>('active');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterCampaign, setFilterCampaign] = useState<string>('all');

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 999,
    assignee_name: '',
    due_date: '',
    project: 'luigis-hall-shame',
    tags: [] as string[],
    campaign_id: null as string | null,
  });

  const [editTask, setEditTask] = useState<Task | null>(null);

  // Fetch tasks
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', filterCompleted, filterProject, filterCampaign],
    queryFn: async () => {
      let query = supabase
        .from('tasks')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_date', { ascending: false });

      if (filterCompleted === 'active') {
        query = query.eq('completed', false);
      } else if (filterCompleted === 'completed') {
        query = query.eq('completed', true);
      }

      if (filterProject !== 'all') {
        query = query.eq('project', filterProject);
      }

      if (filterCampaign === 'none') {
        query = query.is('campaign_id', null);
      } else if (filterCampaign !== 'all') {
        query = query.eq('campaign_id', filterCampaign);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Task[];
    }
  });

  // Fetch campaigns for filter + dropdowns
  const { data: campaigns = [] } = useQuery({
    queryKey: ['social_campaigns_for_tasks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_campaigns' as any)
        .select('id, name, status')
        .order('name', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as unknown) as (CampaignOption & { status?: string })[];
    },
  });

  const campaignNameById = (id?: string | null) =>
    id ? campaigns.find(c => c.id === id)?.name : undefined;

  // Get unique projects for filter
  const projects = ['all', ...new Set(tasks.map(t => t.project).filter(Boolean))];

  // Add task mutation
  const addTaskMutation = useMutation({
    mutationFn: async (task: typeof newTask) => {
      const { error } = await supabase
        .from('tasks')
        .insert([task]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setNewTask({
        title: '',
        description: '',
        priority: 999,
        assignee_name: '',
        due_date: '',
        project: 'luigis-hall-shame',
        tags: [],
        campaign_id: null,
      });
      setIsAddDialogOpen(false);
      toast({ title: "Task created successfully" });
    },
    onError: (error) => {
      toast({
        title: "Failed to create task",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<Task> }) => {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setEditingId(null);
      setEditTask(null);
      toast({ title: "Task updated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Failed to update task",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: "Task deleted successfully" });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete task",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleToggleComplete = (task: Task) => {
    updateTaskMutation.mutate({
      id: task.id,
      updates: {
        completed: !task.completed,
        completed_date: !task.completed ? new Date().toISOString() : null
      }
    });
  };

  const handleEditSave = () => {
    if (editTask) {
      updateTaskMutation.mutate({
        id: editTask.id,
        updates: editTask
      });
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority <= 10) return 'bg-red-500';
    if (priority <= 50) return 'bg-orange-500';
    if (priority <= 100) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getPriorityLabel = (priority: number) => {
    if (priority <= 10) return 'Critical';
    if (priority <= 50) return 'High';
    if (priority <= 100) return 'Medium';
    return 'Low';
  };

  const stats = {
    total: tasks.length,
    active: tasks.filter(t => !t.completed).length,
    completed: tasks.filter(t => t.completed).length,
    overdue: tasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) < new Date()).length
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Tasks</CardDescription>
            <CardTitle className="text-3xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Tasks</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{stats.active}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Completed</CardDescription>
            <CardTitle className="text-3xl text-green-600">{stats.completed}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Overdue</CardDescription>
            <CardTitle className="text-3xl text-red-600">{stats.overdue}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListTodo className="w-5 h-5" />
                Task Management
              </CardTitle>
              <CardDescription>
                Django-todo compatible task system for cross-project management
              </CardDescription>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Task
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Task</DialogTitle>
                  <DialogDescription>
                    Add a new task to your todo list
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="new-title">Title *</Label>
                    <Input
                      id="new-title"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      placeholder="Task title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-description">Description</Label>
                    <Textarea
                      id="new-description"
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      placeholder="Task details..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="new-priority">Priority</Label>
                      <Input
                        id="new-priority"
                        type="number"
                        value={newTask.priority}
                        onChange={(e) => setNewTask({ ...newTask, priority: parseInt(e.target.value) || 999 })}
                        placeholder="1-999 (lower = higher priority)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-due-date">Due Date</Label>
                      <Input
                        id="new-due-date"
                        type="date"
                        value={newTask.due_date}
                        onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="new-assignee">Assignee</Label>
                      <Input
                        id="new-assignee"
                        value={newTask.assignee_name}
                        onChange={(e) => setNewTask({ ...newTask, assignee_name: e.target.value })}
                        placeholder="Assignee name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-project">Project</Label>
                      <Input
                        id="new-project"
                        value={newTask.project}
                        onChange={(e) => setNewTask({ ...newTask, project: e.target.value })}
                        placeholder="Project name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="new-campaign">Campaign</Label>
                    <Select
                      value={newTask.campaign_id ?? 'none'}
                      onValueChange={(v) => setNewTask({ ...newTask, campaign_id: v === 'none' ? null : v })}
                    >
                      <SelectTrigger id="new-campaign">
                        <SelectValue placeholder="No campaign" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No campaign</SelectItem>
                        {campaigns.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => addTaskMutation.mutate(newTask)}
                    disabled={addTaskMutation.isPending || !newTask.title}
                    className="w-full"
                  >
                    Create Task
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4 flex-wrap">
            <Select value={filterCompleted} onValueChange={(v: any) => setFilterCompleted(v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="completed">Completed Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {projects.map(proj => (
                  <SelectItem key={proj} value={proj}>
                    {proj === 'all' ? 'All Projects' : proj}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCampaign} onValueChange={setFilterCampaign}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="All Campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                <SelectItem value="none">No Campaign</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tasks Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      Loading tasks...
                    </TableCell>
                  </TableRow>
                ) : tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No tasks found. Create your first task!
                    </TableCell>
                  </TableRow>
                ) : (
                  tasks.map((task) => (
                    <TableRow key={task.id} className={task.completed ? 'opacity-60' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={task.completed}
                          onCheckedChange={() => handleToggleComplete(task)}
                        />
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Input
                            value={editTask?.title || ''}
                            onChange={(e) => setEditTask(editTask ? {...editTask, title: e.target.value} : null)}
                            className="h-8"
                          />
                        ) : (
                          <div>
                            <div className={task.completed ? 'line-through' : ''}>
                              {task.title}
                            </div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {task.description.slice(0, 60)}...
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Input
                            type="number"
                            value={editTask?.priority || 999}
                            onChange={(e) => setEditTask(editTask ? {...editTask, priority: parseInt(e.target.value) || 999} : null)}
                            className="h-8 w-20"
                          />
                        ) : (
                          <Badge className={getPriorityColor(task.priority)}>
                            {getPriorityLabel(task.priority)} ({task.priority})
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Select
                            value={editTask?.campaign_id ?? 'none'}
                            onValueChange={(v) => setEditTask(editTask ? { ...editTask, campaign_id: v === 'none' ? null : v } : null)}
                          >
                            <SelectTrigger className="h-8 w-[180px]"><SelectValue placeholder="No campaign" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No campaign</SelectItem>
                              {campaigns.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : task.campaign_id ? (
                          <Badge variant="outline" className="text-xs">
                            {campaignNameById(task.campaign_id) ?? 'Unknown campaign'}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Input
                            value={editTask?.assignee_name || ''}
                            onChange={(e) => setEditTask(editTask ? {...editTask, assignee_name: e.target.value} : null)}
                            className="h-8"
                          />
                        ) : (
                          task.assignee_name || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Input
                            type="date"
                            value={editTask?.due_date || ''}
                            onChange={(e) => setEditTask(editTask ? {...editTask, due_date: e.target.value} : null)}
                            className="h-8"
                          />
                        ) : task.due_date ? (
                          <span className={
                            !task.completed && new Date(task.due_date) < new Date()
                              ? 'text-red-600 font-semibold'
                              : ''
                          }>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === task.id ? (
                          <Input
                            value={editTask?.project || ''}
                            onChange={(e) => setEditTask(editTask ? {...editTask, project: e.target.value} : null)}
                            className="h-8"
                          />
                        ) : (
                          <Badge variant="outline">{task.project || 'none'}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.completed ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        ) : (
                          <Badge variant="outline">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {editingId === task.id ? (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleEditSave}
                                disabled={updateTaskMutation.isPending}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditTask(null);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingId(task.id);
                                  setEditTask(task);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm('Delete this task?')) {
                                    deleteTaskMutation.mutate(task.id);
                                  }
                                }}
                                disabled={deleteTaskMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};